import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import cn from 'clsx'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import OccupancyPicker from 'components/Common/OccupancyPicker/OccupancyPicker'
import { validateSemiForm } from 'lib/formUtils'
import { isBedbank, isBundleAndSave, isTourV2Offer } from 'lib/offerTypes'
import {
  getBedbankAndFlightsChildMaxAge,
  getOfferAndFlightsMaxChildAge,
  getOfferMaxInfantAge,
  getTourV2OfferMaxChildAge,
  getTourV2OfferMinAge,
  getBundleAndSaveMaxChildAge,
  getBundleAndSaveMaxInfantAge,
} from 'lib/offerUtils'
import { countOccupantsForProperty, generateRoomOccupants, countOccupantsForBundleAndSave } from 'lib/occupancyUtils'
import { getInteractionStudioDataFromOfferPage } from 'selectors/interactionStudioSelectors'
import { interactionStudioAPIController } from 'lib/interactionStudioUtils'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import config from 'constants/config'
import { isFlightForceBundled } from 'lib/isFlightForceBundled'
import { useTranslation } from 'react-i18next'

const StyledOccupancyPicker = styled(OccupancyPicker)`
  & .occupancy-picker-footer { // TODO: should not be selecting this deep assuming the class exists
    background: ${({ theme }) => theme.palette.neutral.eight};
  }

  ${mediaQueryUp.sm} {
    /**
    * css for offer-capacity-select class
    * on desktop view set the negative margin so that the container div match with its parent container border
    */
    margin: 0 -${rem(24)} -${rem(24)};
    padding-top: ${rem(8)};
  }
`

interface Props {
  rooms?: Array<App.RoomOccupants>;
  offer: App.Offer | App.BedbankOffer | Tours.V2Offer;
  open?: boolean;
  capacities?: Array<App.PackageRoomCapacity>;
  disclaimer?: string;
  interactionStudioData: App.ISACheckoutData
  childrenAgeLabel?: string
  className?: string;
  filter: OfferPageFilterTypes;
  onApply?: () => void
  enableMultiRoomTypesEvents?: boolean;
  bundleOfferId?: string;
}

const defaultRooms = [generateRoomOccupants()]

function OfferCapacityWithAgesSelection(props: Props) {
  const { t } = useTranslation('common')

  const {
    rooms = defaultRooms,
    offer,
    open,
    interactionStudioData,
    capacities,
    disclaimer,
    childrenAgeLabel = t('at time of check-in', { ns: 'common' }),
    className,
    filter,
    onApply,
    enableMultiRoomTypesEvents,
    bundleOfferId,
  } = props
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const pickerRef = useRef<HTMLDivElement>(null)

  const dispatch = useOfferPageDispatch()

  useEffect(() => {
    if (pickerRef.current) {
      if (open) {
        validateSemiForm(pickerRef.current)
      }
    }
  }, [open])

  const onGuestChange = useCallback((nextRooms: Array<App.RoomOccupants>) => {
    if (isBedbank(offer) || isTourV2Offer(offer)) {
      dispatch({ type: OfferPageActions.setRooms, rooms: nextRooms })
    } else {
      dispatch({
        type: OfferPageActions.setRooms,
        rooms: nextRooms.map(({ roomId, ...rest }) => ({
          ...(isBundleAndSave(offer) ? countOccupantsForBundleAndSave([rest], offer) : countOccupantsForProperty([rest], offer.property)),
          roomId,
        })),
      })
    }
  }, [offer, dispatch])

  const handleApply = useCallback(() => {
    dispatch({ type: OfferPageActions.closeFilter, occupancyPicker: true })
    interactionStudioAPIController(filter, interactionStudioData)
    onApply?.()
    trackEvent('OCCUPANCY_FILTERS_APPLIED')
  }, [dispatch, filter, interactionStudioData, onApply, trackEvent])

  const childrenAgeThreshold = useMemo(() => {
    if (isBedbank(offer)) {
      return getBedbankAndFlightsChildMaxAge(offer)
    } else if (isTourV2Offer(offer)) {
      if (offer.isLeTour) {
        return getTourV2OfferMinAge(offer)
      }
      return getTourV2OfferMaxChildAge(offer)
    } else if (isBundleAndSave(offer)) {
      return getBundleAndSaveMaxChildAge(offer)
    } else {
      return getOfferAndFlightsMaxChildAge(offer)
    }
  }, [offer])

  const isLETour = useMemo(() => (isTourV2Offer(offer) && offer.isLeTour), [offer])

  const infantAgeThreshold = useMemo(() => {
    if (isBundleAndSave(offer)) {
      return getBundleAndSaveMaxInfantAge(offer)
    }
    return getOfferMaxInfantAge(offer)
  }, [offer])

  const enableMultiRoom = useMemo(() => {
    if (!config.accommodation.allowsMultiRoom) return false
    if (isBundleAndSave(offer)) return false
    if (isFlightForceBundled(offer)) return false

    return true
  }, [offer])

  return (
    <StyledOccupancyPicker
      ref={pickerRef}
      bundleOfferId={bundleOfferId}
      occupancies={rooms}
      saleUnit={offer.saleUnit}
      onChange={onGuestChange}
      onApply={handleApply}
      maxChildAge={childrenAgeThreshold}
      maxInfantAge={infantAgeThreshold}
      capacities={capacities}
      data-testid="OfferCapacitySelect"
      className={cn(className, 'offer-capacity-select')}
      disclaimer={disclaimer}
      childrenAgeLabel={childrenAgeLabel}
      enableMultiRoomTypesEvents={enableMultiRoomTypesEvents}
      enableMultiRoom={enableMultiRoom}
      adultLabelType={isLETour ? 'traveller' : 'adult'}
      showChildren={!isLETour}
    />
  )
}

export default withOfferPageAndReduxState(
  (state, reduxProps) => ({
    bundleOfferId: state.bundleOffer?.id,
    rooms: state.rooms,
    offer: state.offer ?? state.bedbankOffer ?? state.tourV2Offer,
    open: state.activeFilter === 'capacity',
    filter: state.activeFilter,
    interactionStudioData: getInteractionStudioDataFromOfferPage(reduxProps, state),
  }))(OfferCapacityWithAgesSelection)
